import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import LogoCropDialog from "./LogoCropDialog";
import CnpjTextField from "../../../../../components/react-form/CnpjTextField";
import cnaeSubclasses from "../data/cnae-subclasses.json";
import fpasData from "../data/fpas.json";

const taxRegimeOptions = [
  { value: "simples_nacional", label: "Simples nacional" },
  { value: "lucro_presumido", label: "Lucro presumido" },
  { value: "lucro_real", label: "Lucro real" },
];

const companyHasAgreementsOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const emitBoletoSeparatelyOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const isEmpresaCidadaOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const hasCollectiveVacationOptions = [
  { value: "2", label: "Não" },
  { value: "1", label: "Sim" },
];

const collectiveVacationPeriodOptions = [
  { value: "1", label: "Janeiro" },
  { value: "2", label: "Fevereiro" },
  { value: "3", label: "Março" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Maio" },
  { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
  { value: "13", label: "Início do ano" },
  { value: "14", label: "Meio do ano" },
  { value: "15", label: "Fim do ano" },
];

const collectiveVacationDaysOptions = Array.from({ length: 30 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1} dia${i + 1 > 1 ? "s" : ""}`,
}));

const collectiveVacationByCostCenterOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não, integralmente" },
  { value: "3", label: "Outro, descreva a regra de negócio" },
];

const sectionTitleSx = {
  color: "#7A7A7A",
  fontWeight: "600",
  fontSize: "14px",
  my: 0.5,
};

export default function SocialDataAndCharges({ isStaff }) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [rawLogoB64, setRawLogoB64] = useState("");
  const [logoPreview, setLogoPreview] = useState("");

  const handleLogoFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawLogoB64(reader.result);
      setLogoDialogOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleLogoCropConfirm = base64 => {
    setLogoPreview(base64);
    setValue("companyLogo", base64);
    setLogoDialogOpen(false);
  };

  const companyLogo = watch("companyLogo");

  useEffect(() => {
    if (companyLogo && companyLogo !== logoPreview) {
      setLogoPreview(companyLogo);
    }
  }, [companyLogo, logoPreview]);

  const isValidated = watch("socialDataAndChargesIsValidated");
  const hasCollectiveVacation = watch("hasCollectiveVacation");
  const collectiveVacationByCostCenter = watch("collectiveVacationByCostCenter");
  const companyHasAgreements = watch("companyHasAgreements");
  const emitBoletoSeparately = watch("emitBoletoSeparately");
  const mainCnae = watch("mainCnae");

  const filteredFpas = React.useMemo(() => {
    if (!mainCnae?.id) return [];
    return fpasData.filter(f => Number(f.cnae) === Number(mainCnae.id));
  }, [mainCnae]);

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        mx: { xs: 0, sm: 1, md: 2 },
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Dados sociais & encargos
        </Typography>

        {isStaff && (
          <Controller
            name="socialDataAndChargesIsValidated"
            control={control}
            render={({ field }) => (
              <FormControl error={!!errors.socialDataAndChargesIsValidated}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox {...field} checked={!!field.value} size="small" />
                  <Typography variant="body2">Validado</Typography>
                </Box>
                <FormHelperText>
                  {errors.socialDataAndChargesIsValidated?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        )}
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="companyCnpj"
            control={control}
            render={({ field }) => (
              <CnpjTextField
                {...field}
                required
                disabled={isValidated}
                label="CNPJ"
                fullWidth
                variant="outlined"
                size="small"
                error={!!errors.companyCnpj}
                helperText={errors.companyCnpj?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isValidated}
                required
                label="Nome da empresa"
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            component="fieldset"
            sx={{
              border: "1px solid #c4c4c4",
              borderRadius: 1,
              px: 2,
              pt: 0.5,
              pb: 1.5,
              "&:hover": { borderColor: "#000" },
            }}
          >
            <Box component="legend" sx={{ px: 0.5, fontSize: "12px", color: "#666", width: "auto !important" }}>
              Logo da empresa
            </Box>

            {logoPreview ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo da empresa"
                  sx={{
                    height: 56,
                    maxWidth: 220,
                    objectFit: "contain",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 0.5,
                    bgcolor: "#fafafa",
                  }}
                />
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={isValidated}
                >
                  Alterar logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoFileChange}
                  />
                </Button>
              </Box>
            ) : (
              <Box
                component="label"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  border: "2px dashed #c4c4c4",
                  borderRadius: 1,
                  py: 2,
                  px: 3,
                  cursor: isValidated ? "not-allowed" : "pointer",
                  bgcolor: isValidated ? "#f5f5f5" : "#fafafa",
                  transition: "border-color 0.2s, background-color 0.2s",
                  "&:hover": !isValidated && {
                    borderColor: "primary.main",
                    bgcolor: "primary.50",
                  },
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  disabled={isValidated}
                  onChange={handleLogoFileChange}
                />
                <Typography variant="body2" fontWeight={500} color={isValidated ? "text.disabled" : "text.primary"}>
                  Clique para fazer o upload da logo
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PNG, JPG ou SVG — recomendado fundo transparente
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="taxRegime"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.taxRegime}>
                <InputLabel id="tax-regime-label">Regime tributário</InputLabel>
                <Select
                  {...field}
                  required
                  disabled={isValidated}
                  labelId="tax-regime-label"
                  label="Regime tributário"
                >
                  {taxRegimeOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.taxRegime?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="mainCnae"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={cnaeSubclasses}
                getOptionLabel={option =>
                  option ? `${option.id} - ${option.descricao}` : ""
                }
                value={field.value || null}
                onChange={(_, newValue) => field.onChange(newValue)}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                disabled={isValidated}
                renderInput={params => (
                  <TextField
                    {...params}
                    required
                    label="CNAE preponderante"
                    size="small"
                    error={!!errors.mainCnae}
                    helperText={errors.mainCnae?.message}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="companyHasAgreements"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.companyHasAgreements}>
                <InputLabel id="company-has-agreements-label">
                  Convênios / acordos / ajustes
                </InputLabel>
                <Select
                  {...field}
                  required
                  disabled={isValidated}
                  labelId="company-has-agreements-label"
                  label="Convênios / acordos / ajustes"
                >
                  {companyHasAgreementsOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.companyHasAgreements?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {companyHasAgreements === "1" && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="emitBoletoSeparately"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.emitBoletoSeparately}>
                  <InputLabel id="emit-boleto-separately-label">
                    Emite boleto separadamente
                  </InputLabel>
                  <Select
                    {...field}
                    required
                    disabled={isValidated}
                    labelId="emit-boleto-separately-label"
                    label="Emite boleto separadamente"
                  >
                    {emitBoletoSeparatelyOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.emitBoletoSeparately?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        )}

        {companyHasAgreements === "1" && emitBoletoSeparately === "1" && (
          <Grid item xs={12} sm={6}>
            <Controller
              name="codigoTerceiros"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={filteredFpas}
                  getOptionLabel={option =>
                    option ? `${option.fpas} - ${option.descricao}` : ""
                  }
                  value={field.value || null}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  isOptionEqualToValue={(option, value) =>
                    option.fpas === value?.fpas
                  }
                  disabled={isValidated}
                  noOptionsText={
                    !mainCnae?.id
                      ? "Selecione um CNAE primeiro"
                      : "Nenhuma opção encontrada"
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      required
                      label="Código de terceiros"
                      size="small"
                      error={!!errors.codigoTerceiros}
                      helperText={errors.codigoTerceiros?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Empresa cidadã
        </Typography>

        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="isEmpresaCidada"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.isEmpresaCidada}>
                  <InputLabel id="is-empresa-cidada-label">
                    Conveniada &apos;Empresa cidadã&apos; (Lei 11.770/2008)
                  </InputLabel>
                  <Select
                    {...field}
                    required
                    disabled={isValidated}
                    labelId="is-empresa-cidada-label"
                    label="Conveniada 'Empresa cidadã' (Lei 11.770/2008)"
                  >
                    {isEmpresaCidadaOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.isEmpresaCidada?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          Férias coletivas
        </Typography>

        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="hasCollectiveVacation"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasCollectiveVacation}>
                  <InputLabel id="has-collective-vacation-label">
                    Aplica férias coletivas?
                  </InputLabel>
                  <Select
                    {...field}
                    required
                    disabled={isValidated}
                    labelId="has-collective-vacation-label"
                    label="Aplica férias coletivas?"
                  >
                    {hasCollectiveVacationOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.hasCollectiveVacation?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasCollectiveVacation === "1" && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="collectiveVacationPeriod"
                  control={control}
                  render={({ field }) => {
                    const selected = Array.isArray(field.value) ? field.value : [];
                    return (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disabled={isValidated}
                        options={collectiveVacationPeriodOptions}
                        getOptionLabel={opt => opt.label}
                        value={collectiveVacationPeriodOptions.filter(o => selected.includes(o.value))}
                        isOptionEqualToValue={(opt, val) => opt.value === val.value}
                        getOptionDisabled={() => selected.length >= 2}
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map(o => o.value));
                        }}
                        renderOption={(props, option, { selected: isSelected }) => (
                          <li {...props}>
                            <Checkbox checked={isSelected} size="small" sx={{ mr: 1 }} />
                            <ListItemText primary={option.label} />
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            required
                            label="Período (até 2)"
                            size="small"
                            error={!!errors.collectiveVacationPeriod}
                            helperText={errors.collectiveVacationPeriod?.message}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="collectiveVacationDays"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.collectiveVacationDays}>
                      <InputLabel id="collective-vacation-days-label">Quantidade de dias</InputLabel>
                      <Select
                        {...field}
                        required
                        disabled={isValidated}
                        labelId="collective-vacation-days-label"
                        label="Quantidade de dias"
                      >
                        {collectiveVacationDaysOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.collectiveVacationDays?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="collectiveVacationByCostCenter"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={!!errors.collectiveVacationByCostCenter}
                    >
                      <InputLabel id="collective-vacation-by-cost-center-label">
                        Por centro de custo/setor
                      </InputLabel>
                      <Select
                        {...field}
                        required
                        disabled={isValidated}
                        labelId="collective-vacation-by-cost-center-label"
                        label="Por centro de custo/setor"
                      >
                        {collectiveVacationByCostCenterOptions.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors.collectiveVacationByCostCenter?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {collectiveVacationByCostCenter === "3" && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="collectiveVacationOtherRule"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        disabled={isValidated}
                        label="Descreva a regra de negócio"
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={!!errors.collectiveVacationOtherRule}
                        helperText={errors.collectiveVacationOtherRule?.message}
                      />
                    )}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
      <LogoCropDialog
        open={logoDialogOpen}
        b64Photo={rawLogoB64}
        onConfirm={handleLogoCropConfirm}
        onCancel={() => setLogoDialogOpen(false)}
      />
    </Paper>
  );
}
